import { UseQueryResult } from '@tanstack/react-query';
import { css } from 'aphrodite';
import { createContext, useContext, useEffect, useState } from 'react';
import { IPagedCollection, IUser } from '../../../extViewmodels';
import { getDisplayName } from '../../../models/NameUtils';
import { useUserQuery } from '../../../queries';
import { useLeadServedManagerAutocompleteQuery } from '../../../queries/LeadServed/useLeadServedManagerAutocompleteQuery';
import { useUserAutocompleteQuery } from '../../../queries/User/useUserAutocompleteQuery';
import { Combobox, ComboboxStatus } from '../../components-v2/Combobox';
import { ComboboxAvatarItem } from '../../components-v2/Combobox/ComboboxAvatarItem';
import { bs } from '../../styles/styles';
import { LoadingSpinner } from '../LoadingSpinner';

interface Props extends React.ComponentProps<typeof Combobox> {
	id?: string;
}

const ComboboxSearchInputState = createContext<string>('');

function _UserComboboxStatus({
	userAutocompleteQuery,
	...props
}: { userAutocompleteQuery: UseQueryResult<IPagedCollection<IUser>> } & React.ComponentProps<typeof ComboboxStatus>) {
	const searchInputValue = useContext(ComboboxSearchInputState);
	let status: React.ReactNode = '';
	if (userAutocompleteQuery.isLoading) {
		status = (
			<div className={css(bs.flex, bs.itemsCenter, bs.gap2)}>
				<LoadingSpinner type='extra-tiny' />
				Searching Users...
			</div>
		);
	} else if (userAutocompleteQuery.error) {
		status = userAutocompleteQuery.error.message;
	} else if (userAutocompleteQuery.data?.values?.length === 0 && searchInputValue) {
		status = `No Users found for "${searchInputValue}"`;
	} else if (userAutocompleteQuery.data?.values?.length > 0) {
		status = `${userAutocompleteQuery.data.values.length} User${userAutocompleteQuery.data.values.length === 1 ? '' : 's'} found`;
	} else if (!searchInputValue) {
		status = 'Start typing to search Users...';
	}
	return <ComboboxStatus {...props}>{status}</ComboboxStatus>;
}

export function UserCombobox(props: Props) {
	const { id, ...rest } = props;
	const userQuery = useUserQuery({
		id,
		enabled: Boolean(id),
	});
	const [searchValue, setSearchValue] = useState('');
	const userAutocompleteQuery = useUserAutocompleteQuery({
		fragment: searchValue,
		enabled: Boolean(searchValue),
	});
	useEffect(() => {
		if (!searchValue && userQuery.data) {
			setSearchValue(getDisplayName(userQuery.data));
		}
	}, [searchValue, userQuery.data]);
	return (
		<ComboboxSearchInputState.Provider value={searchValue}>
			<Combobox
				items={userAutocompleteQuery.data?.values ?? []}
				value={userQuery.data ?? null}
				inputValue={searchValue}
				onInputValueChange={setSearchValue}
				filter={null}
				itemToStringLabel={getDisplayName}
				isItemEqualToValue={(item: IUser, value: IUser) => {
					return item.id === value.id;
				}}
				{...rest}
			/>
		</ComboboxSearchInputState.Provider>
	);
}

export function UserComboboxStatus(props: React.ComponentProps<typeof ComboboxStatus>) {
	const searchInputValue = useContext(ComboboxSearchInputState);
	const userAutocompleteQuery = useUserAutocompleteQuery({
		fragment: searchInputValue,
		enabled: Boolean(searchInputValue),
	});
	return <_UserComboboxStatus userAutocompleteQuery={userAutocompleteQuery} {...props} />;
}

export function UserComboboxAvatarItem(
	props: { user: IUser } & Partial<React.ComponentProps<typeof ComboboxAvatarItem>>
) {
	const { user, ...rest } = props;
	return (
		<ComboboxAvatarItem
			value={user}
			avatarImageUrl={user.profilePic}
			avatarFallbackText={user.firstName?.substring(0, 1) + user.lastName?.substring(0, 1)}
			title={getDisplayName(user)}
			description={user.email}
			{...rest}
		/>
	);
}

export function ManagerCombobox(props: Props) {
	const { id, ...rest } = props;
	const userQuery = useUserQuery({
		id,
		enabled: Boolean(id),
	});
	const [searchValue, setSearchValue] = useState('');
	const leadServedManagerAutocompleteQuery = useLeadServedManagerAutocompleteQuery({
		fragment: searchValue,
		enabled: Boolean(searchValue),
	});
	useEffect(() => {
		if (!searchValue && userQuery.data) {
			setSearchValue(getDisplayName(userQuery.data));
		}
	}, [searchValue, userQuery.data]);
	return (
		<ComboboxSearchInputState.Provider value={searchValue}>
			<Combobox
				items={leadServedManagerAutocompleteQuery.data?.values ?? []}
				value={userQuery.data ?? null}
				inputValue={searchValue}
				onInputValueChange={setSearchValue}
				itemToStringLabel={getDisplayName}
				isItemEqualToValue={(item: IUser, value: IUser) => {
					return item.id === value.id;
				}}
				{...rest}
			/>
		</ComboboxSearchInputState.Provider>
	);
}

export function ManagerComboboxStatus(props: React.ComponentProps<typeof ComboboxStatus>) {
	const searchInputValue = useContext(ComboboxSearchInputState);
	const leadServedManagerAutocompleteQuery = useLeadServedManagerAutocompleteQuery({
		fragment: searchInputValue,
		enabled: Boolean(searchInputValue),
	});
	return <_UserComboboxStatus userAutocompleteQuery={leadServedManagerAutocompleteQuery} {...props} />;
}
